import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SEO, { createWebPageSchema, createBreadcrumbSchema } from '../components/SEO.tsx';
import ProjectCard from '../components/projects/ProjectCard.tsx';
import { projectData } from '../data/projectData.ts';

const Work: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const isKorean = i18n.language === 'ko';

  const structuredData = [
    createWebPageSchema({
      title: `${t('pageTitle.projects')} | ${t('companyName')}`,
      description: isKorean
        ? '곰곰이 코딩랩의 프로젝트 포트폴리오를 확인해보세요.'
        : 'Explore our portfolio of successful projects including enterprise platforms, telemedicine solutions, and learning management systems.',
      url: '/projects'
    }),
    createBreadcrumbSchema([
      { name: isKorean ? '홈' : 'Home', url: '/' },
      { name: t('pageTitle.projects'), url: '/projects' }
    ])
  ];

  // Real-time filtering based on search query
  const filteredProjects = useMemo(() => {
    const visibleProjects = projectData.filter(p => !p.hidden);
    if (!searchQuery.trim()) return visibleProjects;

    const query = searchQuery.toLowerCase().trim();
    return visibleProjects.filter(project => {
      const title = isKorean ? project.titleKo : project.title;
      const description = isKorean ? project.descriptionKo : project.description;
      const techString = project.technologies.join(' ').toLowerCase();
      const category = project.category.toLowerCase();

      return (
        title.toLowerCase().includes(query) ||
        description.toLowerCase().includes(query) ||
        techString.includes(query) ||
        category.includes(query)
      );
    });
  }, [searchQuery, isKorean]);

  return (
    <div className="min-h-screen bg-background-primary pt-16">
      <SEO
        title={t('pageTitle.projects')}
        description={isKorean
          ? '곰곰이 코딩랩의 프로젝트 포트폴리오를 확인해보세요.'
          : 'Explore our portfolio of successful projects that have transformed businesses across industries.'}
        keywords={['portfolio', 'projects', 'web development', 'mobile apps', 'case studies', '포트폴리오', '프로젝트']}
        canonicalUrl="/projects"
        ogImage="/images/og-work.jpg"
        ogImageAlt={`${t('companyName')} Portfolio`}
        structuredData={structuredData}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-text-primary mb-3 sm:mb-4">
            {t('work.title')} <span className="text-green-600">{t('work.titleHighlight')}</span>
          </h1>
          <p className="text-base sm:text-xl text-text-secondary max-w-2xl mx-auto px-2">
            {t('work.subtitle')}
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-8 sm:mb-12 max-w-xl mx-auto px-4 sm:px-0">
          <div className="flex items-center bg-white rounded-full border-2 border-gray-200 focus-within:border-green-500 transition-colors pl-4 sm:pl-5 pr-1.5 sm:pr-2 py-1 sm:py-1.5 gap-2 sm:gap-3">
            <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={isKorean ? "프로젝트 검색..." : "Search projects..."}
              className="flex-1 py-1 bg-transparent border-none outline-none focus:outline-none focus:ring-0 text-gray-900 placeholder-gray-400 text-sm sm:text-base"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            <button className="bg-green-600 hover:bg-green-700 text-white px-5 sm:px-7 py-2.5 sm:py-3 font-medium text-base transition-colors rounded-full flex-shrink-0">
              {isKorean ? "검색" : "Search"}
            </button>
          </div>
          {searchQuery && (
            <p className="mt-3 text-sm text-gray-500 text-center">
              {isKorean
                ? `${filteredProjects.length}개의 프로젝트를 찾았습니다`
                : `Found ${filteredProjects.length} project${filteredProjects.length !== 1 ? 's' : ''}`
              }
            </p>
          )}
        </div>

        {/* Projects - Mobile: List / Desktop: Grid */}
        <div className="space-y-3 md:space-y-0 md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-8 mb-16">
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.id}
              id={project.id}
              title={isKorean ? project.titleKo : project.title}
              description={isKorean ? project.descriptionKo : project.description}
              image={project.thumbnail}
              technologies={project.technologies}
              liveUrl={project.liveUrl}
              githubUrl={project.githubUrl}
              category={project.category}
            />
          ))}
        </div>

        {/* Empty State */}
        {filteredProjects.length === 0 && (
          <div className="text-center py-16">
            <div className="inline-block bg-white rounded-2xl border border-gray-200 px-8 py-6 shadow-sm">
              <p className="text-xl text-gray-500">{t('work.empty')}</p>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default Work;
